import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
} from '@/shared/components';
import { DEFAULT_BASE_ASSET, NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
import type { BaseAsset } from '@/shared/types/assetsTypes';
import { cn } from '@/shared/utils/styling';
import { useAccountDetailsActionButtons } from '@/widgets';
import {
  ACCOUNT_TYPES,
  type AccountMarginRest,
  type AccountRest,
  type AccountType,
  useAccountsStore,
} from '@/entities/account';
import { useAccountCreateModal } from '@/widgets';
import { AccountBalance } from './AccountBalance';
import { AccountCardMenu } from './AccountCardMenu';

interface AccountCardProps {
  isBalancesHidden: boolean;
  type: AccountType;
  accountTitle: string;
  balance: number | null;
  currency?: BaseAsset;
  deltaPercent: number | null;
  precision?: number;
  isPending?: boolean;
  className?: string;
  onDeposit: () => void;
  onWithdraw: () => void;
  onInternalTransfer: (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
  }) => void;
  onAccountCardClick?: () => void;
  isAccountCardSelected: boolean;
  canBeSelected?: boolean;
  accounts: Array<AccountRest | AccountMarginRest>;
  onAccountClick: (accountNumber: string) => void;
  onExchange: () => void;
}

const AccountCard = ({
  isBalancesHidden,
  type,
  accountTitle,
  balance,
  currency = DEFAULT_BASE_ASSET,
  deltaPercent,
  precision = NUMERIC_CONFIG.precision.default,
  className,
  isPending,
  onDeposit,
  onWithdraw,
  onInternalTransfer,
  onAccountCardClick,
  isAccountCardSelected,
  canBeSelected = false,
  accounts,
  onAccountClick,
  onExchange,
}: AccountCardProps) => {
  const { t } = useTranslation();

  const { actionButtonsVisibility } = useAccountDetailsActionButtons(type);
  const accountData = useAccountsStore(state => state[type]);
  const { openCreateAccountModal } = useAccountCreateModal(type);

  return (
    <Card
      onClick={onAccountCardClick}
      className={cn(
        'flex flex-col justify-between gap-[0.7rem]',
        'w-[30rem] h-[16rem] overflow-hidden',
        'p-[2.4rem_2rem_2.4rem_3.2rem] rounded-[2.4rem]',
        'relative bg-background shadow-none',
        canBeSelected &&
          'group transition-transform duration-300 ease-in-out hover:shadow-md cursor-pointer',
        isAccountCardSelected && 'translate-y-[-0.8rem] shadow-md',
        className
      )}>
      <CardHeader className="flex items-start justify-between p-0">
        <CardTitle className="headline4 text-primary flex flex-row items-center gap-[1.2rem]">
          {canBeSelected && (
            <Checkbox
              checked={isAccountCardSelected}
              className={cn(
                'w-[2.4rem] h-[2.4rem]',
                'cursor-pointer rounded-full bg-stroke',
                !isAccountCardSelected &&
                  'group-hover:bg-secondary group-hover:text-white',
                isAccountCardSelected &&
                  'bg-primary text-primary-foreground data-[state=checked]:group-hover:bg-secondary data-[state=checked]:group-hover:text-white'
              )}
              forceIndicatorMount={true}
              indicatorClassName="opacity-0 group-hover:opacity-100 data-[state=checked]:opacity-100"
            />
          )}

          {accountTitle}
        </CardTitle>

        {actionButtonsVisibility && (
          <AccountCardMenu
            type={type}
            actionButtonsVisibility={actionButtonsVisibility}
            onDeposit={onDeposit}
            onWithdraw={onWithdraw}
            onInternalTransfer={() =>
              onInternalTransfer({
                accountType: type,
                accountNumber:
                  accountData?.accounts?.length === 1
                    ? accountData?.accounts[0]?.accNum
                    : '',
                assetCode: '',
              })
            }
            onOpenNewAccount={openCreateAccountModal}
            onExchange={onExchange}
            accountTitle={accountTitle}
            accounts={accounts.filter(
              account => account.accTypeName === ACCOUNT_TYPES.Trading
            )}
            precision={precision}
            onAccountClick={onAccountClick}
          />
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-[0.4rem] p-0">
        <span className="medium6 text-secondary">
          {t('personalAccount.accountsSection.cardBalanceTitle')}
        </span>

        <AccountBalance
          isBalancesHidden={isBalancesHidden}
          balance={balance}
          deltaPercent={deltaPercent}
          currency={currency}
          precision={precision}
          isPending={isPending}
        />
      </CardContent>
    </Card>
  );
};

export { AccountCard };
